/**
 * Pick up a deploy without being asked.
 *
 * An installed app can sit open for days on a bundle that shipped last week, and the usual
 * service-worker update signal never fires here because `sw.js` is a static file whose bytes do
 * not change between deploys. So the app asks the server when it was built and compares.
 *
 * Two things stop this becoming a reload loop: the check is "strictly newer than me", which
 * settles itself once the new bundle loads, and a stamp is remembered for the tab so a reload
 * that somehow serves the old bundle again is not retried forever.
 */

import { useEffect } from "react";
import { BUILD_ID, BUILT_AT_MS } from "./build";
import { report } from "./report";

/** Long enough to be free, short enough that a morning's deploy lands before the evening. */
const CHECK_EVERY_MS = 15 * 60_000;
const ATTEMPTED_KEY = "gotime.reloadedFor";

function alreadyTried(deployed: number): boolean {
	try {
		return sessionStorage.getItem(ATTEMPTED_KEY) === String(deployed);
	} catch {
		return false;
	}
}

function rememberAttempt(deployed: number): void {
	try {
		sessionStorage.setItem(ATTEMPTED_KEY, String(deployed));
	} catch {
		// Private browsing. Worst case the check runs again on the next interval.
	}
}

async function deployedVersion(): Promise<{ builtAtMs: number; buildId: string | null } | null> {
	try {
		const response = await fetch("/api/version", { cache: "no-store" });
		if (!response.ok) {
			return null;
		}
		const body = (await response.json()) as { builtAtMs?: unknown; buildId?: unknown };
		return typeof body.builtAtMs === "number" ? { builtAtMs: body.builtAtMs, buildId: typeof body.buildId === "string" ? body.buildId : null } : null;
	} catch {
		return null;
	}
}

export function useVersionWatch(): void {
	useEffect(() => {
		// Without a build date of its own there is nothing to compare, and a dev build must never
		// reload itself out from under a debugger.
		const running = BUILT_AT_MS;
		if (running == null) {
			return;
		}

		let cancelled = false;

		const check = async () => {
			const latest = await deployedVersion();
			// Strictly newer, so a clock skew or a rollback can never start a reload loop.
			if (cancelled || latest == null || latest.builtAtMs <= running || alreadyTried(latest.builtAtMs)) {
				return;
			}
			rememberAttempt(latest.builtAtMs);
			report("version-stale", { running, deployed: latest.builtAtMs, runningBuild: BUILD_ID, deployedBuild: latest.buildId });
			window.location.reload();
		};

		const onVisible = () => {
			if (document.visibilityState === "visible") {
				void check();
			}
		};

		void check();
		const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
		document.addEventListener("visibilitychange", onVisible);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, []);
}
